import type { FormEvent } from 'react';
import { useState } from 'react';
import { useApp } from '@/context/AppContext';
import { useData } from '@/context/DataContext';
import { supabase } from '@/lib/supabase';
import Modal from '@/components/base/Modal';
import { Field, Input, Select, Textarea } from '@/components/base/FormField';
import {
  projectCategories,
  sectors,
  taskStatuses,
  taskPriorities,
  paymentTypes,
  purchaseMaterialStatuses,
  purchasePaymentStatuses,
} from '@/types';

const cancelClass =
  'cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100';
const saveClass =
  'cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60';

const team = ['Fatih Demir', 'Emre Arslan', 'Ahmet Yıldız', 'Mehmet Kaya', 'Cem Özkan'];

export default function CreateModals() {
  const { createType, closeCreate, showToast } = useApp();
  const { customers, projects, quotes, refresh, projectById } = useData();
  const [busy, setBusy] = useState(false);

  const orNull = (v: string) => (v === '' ? null : v);

  const handle =
    (
      table: string,
      build: (get: (n: string) => string, getNum: (n: string) => number) => Record<string, unknown>,
      message: string,
    ) =>
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const fd = new FormData(e.currentTarget);
      const get = (n: string) => String(fd.get(n) ?? '').trim();
      const getNum = (n: string) => Number(fd.get(n) ?? 0) || 0;
      const payload = build(get, getNum);
      setBusy(true);
      const { error } = await supabase.from(table).insert(payload);
      setBusy(false);
      if (error) {
        showToast('Kaydedilemedi: ' + error.message);
      } else {
        showToast(message);
        closeCreate();
        refresh();
      }
    };

  const nextQuoteNumber = () => {
    const year = new Date().getFullYear();
    return `TKL-${year}-${String(quotes.length + 1).padStart(3, '0')}`;
  };

  const customerOptions = customers.map((c) => (
    <option key={c.id} value={c.id}>
      {c.name}
    </option>
  ));
  const projectOptions = projects.map((p) => (
    <option key={p.id} value={p.id}>
      {p.name}
    </option>
  ));

  return (
    <>
      <Modal open={createType === 'customer'} onClose={closeCreate} title="Yeni Müşteri" subtitle="Müşteri bilgilerini girin" size="lg">
        <form
          onSubmit={handle(
            '3dmachine_customers',
            (get) => ({
              name: get('name'),
              contact_person: get('contactPerson'),
              phone: get('phone'),
              email: get('email'),
              city: get('city'),
              sector: get('sector'),
              address: get('address'),
              notes: get('notes'),
            }),
            'Müşteri kaydedildi',
          )}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Firma Adı" required><Input name="name" required placeholder="Firma unvanı" /></Field>
            <Field label="Yetkili Kişi" required><Input name="contactPerson" required placeholder="Ad Soyad" /></Field>
            <Field label="Telefon"><Input name="phone" placeholder="05xx xxx xx xx" /></Field>
            <Field label="E-posta"><Input name="email" type="email" placeholder="ornek@firma.com" /></Field>
            <Field label="Şehir"><Input name="city" placeholder="İl" /></Field>
            <Field label="Sektör">
              <Select name="sector" defaultValue="">{sectors.map((s) => <option key={s}>{s}</option>)}</Select>
            </Field>
            <Field label="Adres" className="sm:col-span-2"><Input name="address" placeholder="Açık adres" /></Field>
            <Field label="Notlar" className="sm:col-span-2"><Textarea name="notes" placeholder="Ek notlar" /></Field>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={closeCreate} className={cancelClass}>Vazgeç</button>
            <button type="submit" disabled={busy} className={saveClass}>Kaydet</button>
          </div>
        </form>
      </Modal>

      <Modal open={createType === 'quote'} onClose={closeCreate} title="Yeni Teklif" subtitle="Teklif bilgilerini girin" size="lg">
        <form
          onSubmit={handle(
            '3dmachine_quotes',
            (get, getNum) => {
              const amount = getNum('amount');
              const estimatedCost = getNum('estimatedCost');
              return {
                number: nextQuoteNumber(),
                date: orNull(get('date')),
                customer_id: orNull(get('customerId')),
                project_name: get('projectName'),
                category: get('category'),
                amount,
                estimated_cost: estimatedCost,
                estimated_profit: amount - estimatedCost,
                validity_date: orNull(get('validityDate')),
                status: 'Taslak',
                description: get('description'),
                tax_status: get('taxStatus'),
                payment_plan: get('paymentPlan'),
                delivery_time: get('deliveryTime'),
                technical_notes: get('technicalNotes'),
                internal_note: get('internalNote'),
              };
            },
            'Teklif oluşturuldu',
          )}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Müşteri" required>
              <Select name="customerId" required defaultValue="">
                <option value="">Seçiniz</option>
                {customerOptions}
              </Select>
            </Field>
            <Field label="Proje Adı" required><Input name="projectName" required placeholder="Proje / iş adı" /></Field>
            <Field label="İş Kategorisi">
              <Select name="category" defaultValue="">{projectCategories.map((c) => <option key={c}>{c}</option>)}</Select>
            </Field>
            <Field label="Teklif Tarihi"><Input name="date" type="date" /></Field>
            <Field label="Geçerlilik Tarihi"><Input name="validityDate" type="date" /></Field>
            <Field label="Teklif Tutarı (₺)"><Input name="amount" type="number" placeholder="0" /></Field>
            <Field label="Vergi Durumu">
              <Select name="taxStatus" defaultValue="KDV Dahil"><option>KDV Dahil</option><option>KDV Hariç</option><option>Muaf</option></Select>
            </Field>
            <Field label="Tahmini Maliyet (₺)"><Input name="estimatedCost" type="number" placeholder="0" /></Field>
            <Field label="Ödeme Planı" className="sm:col-span-2"><Input name="paymentPlan" placeholder="Örn: %30 kapora, %40 ara ödeme, %30 teslim" /></Field>
            <Field label="Teslim Süresi"><Input name="deliveryTime" placeholder="Örn: 6 hafta" /></Field>
            <Field label="Talep Açıklaması" className="sm:col-span-2"><Textarea name="description" placeholder="Müşteri talebi" /></Field>
            <Field label="Teknik Açıklamalar" className="sm:col-span-2"><Textarea name="technicalNotes" placeholder="Teknik detaylar" /></Field>
            <Field label="İç Not" className="sm:col-span-2"><Textarea name="internalNote" placeholder="Sadece ekip içi not" /></Field>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={closeCreate} className={cancelClass}>Vazgeç</button>
            <button type="submit" disabled={busy} className={saveClass}>Teklif Oluştur</button>
          </div>
        </form>
      </Modal>

      <Modal open={createType === 'project'} onClose={closeCreate} title="Yeni Proje" subtitle="Proje bilgilerini girin" size="lg">
        <form
          onSubmit={handle(
            '3dmachine_projects',
            (get, getNum) => ({
              name: get('name'),
              customer_id: orNull(get('customerId')),
              category: get('category'),
              responsible: get('responsible'),
              start_date: orNull(get('startDate')),
              planned_delivery: orNull(get('plannedDelivery')),
              sales_amount: getNum('salesAmount'),
              status: 'Devam Ediyor',
              progress: 0,
              current_stage: 'Proje Alındı',
              description: get('description'),
            }),
            'Proje oluşturuldu',
          )}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Proje Adı" required><Input name="name" required placeholder="Proje adı" /></Field>
            <Field label="Müşteri" required>
              <Select name="customerId" required defaultValue="">
                <option value="">Seçiniz</option>
                {customerOptions}
              </Select>
            </Field>
            <Field label="Proje Kategorisi"><Select name="category" defaultValue="">{projectCategories.map((c) => <option key={c}>{c}</option>)}</Select></Field>
            <Field label="Proje Sorumlusu"><Select name="responsible" defaultValue=""><option value="">Seçiniz</option>{team.map((t) => <option key={t}>{t}</option>)}</Select></Field>
            <Field label="Başlangıç Tarihi"><Input name="startDate" type="date" /></Field>
            <Field label="Planlanan Teslim"><Input name="plannedDelivery" type="date" /></Field>
            <Field label="Satış Tutarı (₺)"><Input name="salesAmount" type="number" placeholder="0" /></Field>
            <Field label="Açıklama" className="sm:col-span-2"><Textarea name="description" placeholder="Proje kapsamı" /></Field>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={closeCreate} className={cancelClass}>Vazgeç</button>
            <button type="submit" disabled={busy} className={saveClass}>Proje Oluştur</button>
          </div>
        </form>
      </Modal>

      <Modal open={createType === 'task'} onClose={closeCreate} title="Yeni Görev" subtitle="Görev bilgilerini girin">
        <form
          onSubmit={handle(
            '3dmachine_tasks',
            (get) => {
              const pid = get('projectId');
              const customerId = pid ? projectById(pid)?.customerId ?? null : null;
              return {
                title: get('title'),
                customer_id: customerId,
                project_id: orNull(pid),
                assignee: get('assignee'),
                start_date: orNull(get('startDate')),
                due_date: orNull(get('dueDate')),
                priority: get('priority') || 'Normal',
                status: get('status') || 'Yapılacak',
              };
            },
            'Görev oluşturuldu',
          )}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Görev" required><Input name="title" required placeholder="Görev tanımı" /></Field>
            <Field label="Sorumlu"><Select name="assignee" defaultValue=""><option value="">Seçiniz</option>{team.map((t) => <option key={t}>{t}</option>)}</Select></Field>
            <Field label="Bağlı Proje"><Select name="projectId" defaultValue=""><option value="">Seçiniz</option>{projectOptions}</Select></Field>
            <Field label="Öncelik"><Select name="priority" defaultValue="Normal">{taskPriorities.map((p) => <option key={p}>{p}</option>)}</Select></Field>
            <Field label="Başlangıç"><Input name="startDate" type="date" /></Field>
            <Field label="Son Tarih"><Input name="dueDate" type="date" /></Field>
            <Field label="Durum"><Select name="status" defaultValue="Yapılacak">{taskStatuses.map((s) => <option key={s}>{s}</option>)}</Select></Field>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={closeCreate} className={cancelClass}>Vazgeç</button>
            <button type="submit" disabled={busy} className={saveClass}>Kaydet</button>
          </div>
        </form>
      </Modal>

      <Modal open={createType === 'collection'} onClose={closeCreate} title="Yeni Tahsilat" subtitle="Tahsilat kaydı oluşturun">
        <form
          onSubmit={handle(
            '3dmachine_collections',
            (get, getNum) => ({
              date: orNull(get('date')),
              customer_id: orNull(get('customerId')),
              project_id: orNull(get('projectId')),
              payment_type: get('paymentType'),
              amount: getNum('amount'),
              description: get('description'),
            }),
            'Tahsilat kaydedildi',
          )}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Tarih"><Input name="date" type="date" /></Field>
            <Field label="Müşteri" required>
              <Select name="customerId" required defaultValue=""><option value="">Seçiniz</option>{customerOptions}</Select>
            </Field>
            <Field label="Proje"><Select name="projectId" defaultValue=""><option value="">Seçiniz</option>{projectOptions}</Select></Field>
            <Field label="Ödeme Tipi"><Select name="paymentType" defaultValue="Ara ödeme">{paymentTypes.map((p) => <option key={p}>{p}</option>)}</Select></Field>
            <Field label="Tutar (₺)" required><Input name="amount" required type="number" placeholder="0" /></Field>
            <Field label="Açıklama"><Input name="description" placeholder="Açıklama" /></Field>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={closeCreate} className={cancelClass}>Vazgeç</button>
            <button type="submit" disabled={busy} className={saveClass}>Kaydet</button>
          </div>
        </form>
      </Modal>

      <Modal open={createType === 'payment'} onClose={closeCreate} title="Yeni Ödeme" subtitle="Ödeme kaydı oluşturun">
        <form
          onSubmit={handle(
            '3dmachine_payments',
            (get, getNum) => ({
              date: orNull(get('date')),
              supplier: get('supplier'),
              project_id: orNull(get('projectId')),
              category: get('category'),
              amount: getNum('amount'),
              description: get('description'),
            }),
            'Ödeme kaydedildi',
          )}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Tarih"><Input name="date" type="date" /></Field>
            <Field label="Tedarikçi" required><Input name="supplier" required placeholder="Tedarikçi adı" /></Field>
            <Field label="Proje"><Select name="projectId" defaultValue=""><option value="">Seçiniz</option>{projectOptions}</Select></Field>
            <Field label="Kategori"><Select name="category" defaultValue="Malzeme"><option>Malzeme</option><option>İşçilik</option><option>Nakliye</option><option>Genel Gider</option></Select></Field>
            <Field label="Tutar (₺)" required><Input name="amount" required type="number" placeholder="0" /></Field>
            <Field label="Açıklama"><Input name="description" placeholder="Açıklama" /></Field>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={closeCreate} className={cancelClass}>Vazgeç</button>
            <button type="submit" disabled={busy} className={saveClass}>Kaydet</button>
          </div>
        </form>
      </Modal>

      <Modal open={createType === 'purchase'} onClose={closeCreate} title="Yeni Satın Alma" subtitle="Satın alma talebi oluşturun" size="lg">
        <form
          onSubmit={handle(
            '3dmachine_purchases',
            (get, getNum) => {
              const quantity = getNum('quantity') || 1;
              const unitPrice = getNum('unitPrice');
              return {
                project_id: orNull(get('projectId')),
                supplier: get('supplier'),
                material: get('material'),
                brand_model: get('brandModel'),
                quantity,
                unit_price: unitPrice,
                total: quantity * unitPrice,
                order_date: orNull(get('orderDate')),
                expected_delivery: orNull(get('expectedDelivery')),
                payment_status: get('paymentStatus'),
                material_status: get('materialStatus'),
              };
            },
            'Satın alma kaydedildi',
          )}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Proje" required>
              <Select name="projectId" required defaultValue=""><option value="">Seçiniz</option>{projectOptions}</Select>
            </Field>
            <Field label="Tedarikçi" required><Input name="supplier" required placeholder="Tedarikçi adı" /></Field>
            <Field label="Malzeme / Ürün" required><Input name="material" required placeholder="Malzeme adı" /></Field>
            <Field label="Marka / Model"><Input name="brandModel" placeholder="Marka ve model" /></Field>
            <Field label="Adet"><Input name="quantity" type="number" placeholder="1" /></Field>
            <Field label="Birim Fiyat (₺)"><Input name="unitPrice" type="number" placeholder="0" /></Field>
            <Field label="Sipariş Tarihi"><Input name="orderDate" type="date" /></Field>
            <Field label="Beklenen Teslim"><Input name="expectedDelivery" type="date" /></Field>
            <Field label="Ödeme Durumu"><Select name="paymentStatus" defaultValue="Ödenmedi">{purchasePaymentStatuses.map((s) => <option key={s}>{s}</option>)}</Select></Field>
            <Field label="Malzeme Durumu"><Select name="materialStatus" defaultValue="Talep Oluşturuldu">{purchaseMaterialStatuses.map((s) => <option key={s}>{s}</option>)}</Select></Field>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={closeCreate} className={cancelClass}>Vazgeç</button>
            <button type="submit" disabled={busy} className={saveClass}>Kaydet</button>
          </div>
        </form>
      </Modal>

      <Modal open={createType === 'service'} onClose={closeCreate} title="Yeni Servis Kaydı" subtitle="Servis / revizyon talebi oluşturun" size="lg">
        <form
          onSubmit={handle(
            '3dmachine_service_records',
            (get, getNum) => ({
              customer_id: orNull(get('customerId')),
              project_id: orNull(get('projectId')),
              request_date: orNull(get('requestDate')),
              issue: get('issue'),
              priority: get('priority') || 'Normal',
              assignee: get('assignee'),
              planned_date: orNull(get('plannedDate')),
              warranty: get('warranty') || 'Garanti',
              fee: getNum('fee'),
              status: 'Yeni Talep',
            }),
            'Servis kaydı oluşturuldu',
          )}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Müşteri" required>
              <Select name="customerId" required defaultValue=""><option value="">Seçiniz</option>{customerOptions}</Select>
            </Field>
            <Field label="Makine / Proje"><Select name="projectId" defaultValue=""><option value="">Seçiniz</option>{projectOptions}</Select></Field>
            <Field label="Talep Tarihi"><Input name="requestDate" type="date" /></Field>
            <Field label="Öncelik"><Select name="priority" defaultValue="Normal">{taskPriorities.map((p) => <option key={p}>{p}</option>)}</Select></Field>
            <Field label="Sorumlu"><Select name="assignee" defaultValue=""><option value="">Seçiniz</option>{team.map((t) => <option key={t}>{t}</option>)}</Select></Field>
            <Field label="Planlanan Servis Tarihi"><Input name="plannedDate" type="date" /></Field>
            <Field label="Garanti Durumu"><Select name="warranty" defaultValue="Garanti"><option>Garanti</option><option>Ücretli</option></Select></Field>
            <Field label="Servis Bedeli (₺)"><Input name="fee" type="number" placeholder="0" /></Field>
            <Field label="Sorun" className="sm:col-span-2"><Textarea name="issue" placeholder="Arıza / talep açıklaması" /></Field>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={closeCreate} className={cancelClass}>Vazgeç</button>
            <button type="submit" disabled={busy} className={saveClass}>Kaydet</button>
          </div>
        </form>
      </Modal>
    </>
  );
}